'use client';
import { AnimatePresence, motion } from 'framer-motion';
import React, { Fragment, useState } from 'react';
import { Button } from '@material-tailwind/react';
import Link from 'next/link';

const JobCard = ({
    jobTitle,
    jobDescription,
    name,
    department,
    designation,
}) => {
    const [showPopup, setShowPopup] = useState(false);

    const jobIcons = {
        accounts: 'money-rupee-circle-line',
        manager: 'speak-line',
        consultant: 'presentation-line',
        developer: 'code-s-slash-line',
        accountant: 'money-rupee-circle-line',
        socialMedia: 'smartphone-line',
        sales: 'customer-service-2-line',
        briefcase: 'briefcase-line',
        brush: 'brush-line',
    };

    function getIcon(jobTitle) {
        if (jobTitle.includes('Manager')) {
            return jobIcons.manager;
        } else if (jobTitle.includes('Accounts')) {
            return jobIcons.accounts;
        } else if (jobTitle.includes('Consultant')) {
            return jobIcons.consultant;
        } else if (jobTitle.includes('Developer')) {
            return jobIcons.developer;
        } else if (jobTitle.includes('Social Media')) {
            return jobIcons.socialMedia;
        } else if (jobTitle.includes('Digital Marketing')) {
            return jobIcons.socialMedia;
        } else if (jobTitle.includes('Sales')) {
            return jobIcons.sales;
        } else if (jobTitle.includes('Graphic Designer')) {
            return jobIcons.brush;
        } else {
            return jobIcons.briefcase;
        }
    }

    const togglePopup = () => {
        setShowPopup(!showPopup);
    };
    return (
        <Fragment>
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                whileHover={{ y: -4 }}
            >
                <div className="rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-blue-100 transition-all duration-300 bg-white p-4 sm:p-6 relative overflow-hidden group flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                    <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-gradient-to-br from-[#57CCF9]/10 to-[#4695F0]/10 group-hover:scale-125 transition-transform duration-500"></div>

                    <span className="inline-flex items-center justify-center h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-gradient-to-br from-[#4695F0] to-[#57CCF9] shadow-md shadow-blue-100 shrink-0 relative">
                        <i
                            className={`ri-${getIcon(
                                String(jobTitle).toLowerCase(),
                            )} text-2xl sm:text-3xl text-white`}
                        ></i>
                    </span>

                    <div className="flex-1 min-w-0 relative">
                        <h1 className="text-lg sm:text-xl font-semibold text-gray-900 break-words">
                            {jobTitle}
                        </h1>

                        {(department || designation) && (
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                {designation && (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-full px-3 py-1">
                                        <i className="ri-award-line"></i>
                                        {designation}
                                    </span>
                                )}
                                {department && (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-3 py-1">
                                        <i className="ri-building-4-line"></i>
                                        {department}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 relative shrink-0 w-full sm:w-auto">
                        <Button
                            onClick={togglePopup}
                            className="w-full sm:w-auto flex items-center justify-center gap-1 py-2.5 px-6 bg-gray-100 text-[#2e2e2e] text-sm font-bold rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            {showPopup ? (
                                <i className="ri-arrow-up-s-line"></i>
                            ) : (
                                <i className="ri-arrow-down-s-line"></i>
                            )}
                            Read Description
                        </Button>
                        <Link
                            className="w-full sm:w-auto"
                            rel="canonical"
                            href={{
                                pathname: '/apply-job',
                                query: { jobId: name },
                            }}
                        >
                            <Button className="w-full sm:w-auto flex items-center justify-center gap-2 py-2.5 px-6 bg-gradient-to-r from-[#4695F0] to-[#57CCF9] text-white text-sm font-bold rounded-lg shadow-md shadow-blue-100 hover:shadow-lg transition-shadow">
                                <i className="ri-send-plane-2-line"></i>
                                Apply Now
                            </Button>
                        </Link>
                    </div>
                </div>
            </motion.div>
            <AnimatePresence>
                {showPopup && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
                        onClick={togglePopup}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 40, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 20, scale: 0.98 }}
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                            className="w-full sm:max-w-2xl max-h-[85vh] sm:max-h-[80vh] overflow-hidden bg-white rounded-t-2xl sm:rounded-2xl relative shadow-2xl flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="bg-gradient-to-r from-[#4695F0] to-[#57CCF9] px-5 sm:px-6 py-4 sm:py-5 flex items-center gap-3">
                                <span className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white/20 shrink-0">
                                    <i
                                        className={`ri-${getIcon(
                                            String(jobTitle).toLowerCase(),
                                        )} text-xl text-white`}
                                    ></i>
                                </span>
                                <h2 className="text-base sm:text-lg font-semibold text-white pr-8 flex-1">
                                    {jobTitle}
                                </h2>
                                <button
                                    type="button"
                                    onClick={togglePopup}
                                    aria-label="Close"
                                    className="text-2xl text-white/90 hover:text-white transition-colors"
                                >
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                            <div
                                className="w-full text-sm sm:text-base leading-relaxed p-5 sm:p-6 overflow-y-auto"
                                dangerouslySetInnerHTML={{
                                    __html: jobDescription,
                                }}
                            ></div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </Fragment>
    );
};

export default JobCard;
